import axiosInstance from '../../../../config/axios';

export interface DatevConnectionStatus {
  connected: boolean;
  consultant_number?: string | null;
  client_number?: string | null;
  connected_at?: string | null;
  expires_at?: string | null;
  env?: 'sandbox' | 'production' | null;
  clients_reachable?: boolean;
  default_client_id?: string | null;
  default_client_name?: string | null;
  product?: 'LuG' | 'LODAS' | null;
  transport?: 'manual' | 'hr:files' | null;
  last_error?: 'token_client_mismatch' | 'clients_forbidden' | 'clients_error' | string | null;
  trace_id?: string | null;
  clients_count?: number | null;
  // Backwards compatibility
  berater_nr?: string | null;
  mandanten_nr?: string | null;
}

export interface DatevClient {
  id: string;
  name: string;
  number?: string;
}

export interface DatevClientsResponse {
  clients: DatevClient[];
  count: number;
  default_client_id?: string | null;
  trace_id?: string | null;
  source?: 'hr-exports' | 'hr-payrollreports' | null;
  fallback_used?: boolean;
}

export interface DatevChooseClientPayload {
  client_id: string;
}

export interface DatevConnectionStartResponse {
  authorize_url: string;
  state?: string;
}

export interface DatevTestResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export interface DatevHistoryEntry {
  id: number;
  batch_id?: string;
  type: 'movements' | 'absences' | 'results' | 'reports';
  file_name: string;
  checksum?: string;
  created_at: string;
  created_by?: number | null;
  created_by_name?: string | null;
}

class DatevService {
  private static readonly BASE = '/api/integrations/datev';

  /**
   * Get current DATEV connection status
   */
  static async getConnectionStatus(): Promise<DatevConnectionStatus> {
    const { data } = await axiosInstance.get(`${DatevService.BASE}/connection/status/`);
    return data;
  }

  /**
   * Start DATEV connection flow (OAuth2)
   * Returns authorize_url to open in new tab
   */
  static async startConnection(params: {
    env?: 'sandbox' | 'production';
    consultant_number?: string;
    client_number?: string;
    // Backwards compatibility
    berater_nr?: string;
    mandanten_nr?: string;
  }): Promise<DatevConnectionStartResponse> {
    const { data } = await axiosInstance.post(`${DatevService.BASE}/connection/start/`, params);
    return data;
  }

  /**
   * Test DATEV connection
   */
  static async testConnection(): Promise<DatevTestResponse> {
    const { data } = await axiosInstance.post(`${DatevService.BASE}/test/`);
    return data;
  }

  /**
   * Get DATEV export/import history
   */
  static async getHistory(filters?: {
    month?: string;
    type?: 'movements' | 'absences' | 'results' | 'reports';
  }): Promise<DatevHistoryEntry[]> {
    const { data } = await axiosInstance.get(`${DatevService.BASE}/history/`, {
      params: filters,
    });
    return Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
  }

  /**
   * Disconnect DATEV integration
   */
  static async disconnect(): Promise<void> {
    await axiosInstance.post(`${DatevService.BASE}/connection/disconnect/`, {});
  }

  /**
   * Get list of available DATEV clients
   */
  static async getClients(): Promise<DatevClientsResponse> {
    try {
      const { data } = await axiosInstance.get(`${DatevService.BASE}/clients/`);
      return {
        clients: Array.isArray(data?.clients) ? data.clients : Array.isArray(data) ? data : [],
        count: data?.count || (Array.isArray(data?.clients) ? data.clients.length : 0),
        default_client_id: data?.default_client_id || null,
        trace_id: data?.trace_id || null,
      };
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.error;
      const detail = err?.response?.data?.detail;
      const trace = err?.response?.data?.trace_id;

      if (status === 403 && (code === 'clients_forbidden' || detail === 'clients_forbidden')) {
        const error: any = new Error('no-client-rights');
        error.response = err.response;
        error.trace_id = trace;
        throw error;
      }
      const error: any = new Error('clients-technical-error');
      error.response = err.response;
      error.trace_id = trace;
      throw error;
    }
  }

  /**
   * Choose default DATEV client
   */
  static async chooseClient(payload: DatevChooseClientPayload): Promise<void> {
    await axiosInstance.post(`${DatevService.BASE}/clients/choose/`, payload);
  }
}

export default DatevService;
